import Papa from 'papaparse'
import type { Question, Source, StudySupport } from '../types'

interface ContentBundleResponse {
  ok?: boolean
  files?: Record<string, Record<string, string>[]>
}

let remoteBundlePromise: Promise<Record<string, Record<string, string>[]>> | null = null

function parseJson<T>(value: string, fallback: T): T {
  if (!value.trim()) return fallback
  try { return JSON.parse(value) as T } catch { return fallback }
}

function toQuestion(row: Record<string, string>): Question {
  return {
    id: row.id ?? '', unit: row.unit ?? 'Unidad 3', oa: row.oa ?? 'OA 07',
    classNumber: Number(row.class_number ?? 0), topic: row.topic ?? '',
    type: (row.type ?? 'multiple_choice') as Question['type'],
    difficulty: (row.difficulty ?? 'Básica') as Question['difficulty'], skill: row.skill ?? '',
    prompt: row.prompt ?? '', adaptedPrompt: row.adapted_prompt ?? '',
    options: parseJson<string[]>(row.options ?? '', []),
    correctAnswer: parseJson<string | string[] | Record<string, string>>(row.correct_answer ?? '', row.correct_answer ?? ''),
    hint: row.hint ?? '', feedback: row.feedback ?? '',
    distractorFeedback: parseJson<Record<string, string>>(row.distractor_feedback ?? '', {}),
    sourceId: row.source_id ?? '', keywords: parseJson<string[]>(row.keywords ?? '', []),
    expectedAnswer: row.expected_answer ?? '',
    selfAssessmentCriteria: parseJson<string[]>(row.self_assessment_criteria ?? '', []),
    stimulus: row.stimulus ?? '', stimulusType: (row.stimulus_type ?? 'none') as Question['stimulusType'],
    stimulusData: parseJson<unknown>(row.stimulus_data ?? '', null), imagePath: row.image_path ?? '',
  }
}

async function loadRemoteBundle(): Promise<Record<string, Record<string, string>[]>> {
  if (!remoteBundlePromise) {
    remoteBundlePromise = fetch('/api/content-data', { cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) return {}
        const data = await response.json() as ContentBundleResponse
        return data.ok && data.files ? data.files : {}
      })
      .catch(() => ({}))
  }
  return remoteBundlePromise
}

async function fetchStaticCsv(fileName: string): Promise<Record<string, string>[]> {
  const path = `/content/${fileName}`
  const response = await fetch(path)
  if (!response.ok) throw new Error(`No se pudo cargar ${path}`)
  const parsed = Papa.parse<Record<string, string>>(await response.text(), {
    header: true, skipEmptyLines: true, transformHeader: (header) => header.trim(),
  })
  if (parsed.errors.length > 0) throw new Error(`El archivo ${path} contiene errores de formato.`)
  return parsed.data
}

export async function fetchContentRows(fileName: string): Promise<Record<string, string>[]> {
  const remote = await loadRemoteBundle()
  const rows = remote[fileName]
  if (Array.isArray(rows) && rows.length > 0) return rows
  return fetchStaticCsv(fileName)
}

export async function loadQuestions(): Promise<Question[]> {
  const manifest = await fetchContentRows('question_files.csv')
  const names = manifest.filter((row) => !['false', '0', 'no'].includes(String(row.active ?? '').toLowerCase())).map((row) => row.file).filter((file): file is string => Boolean(file))
  const files = await Promise.all(names.map((file) => fetchContentRows(file)))
  const questions = files.flat().map(toQuestion)
  validateQuestions(questions)
  return questions
}

export async function loadSources(): Promise<Source[]> {
  return (await fetchContentRows('sources.csv')).map((row) => ({
    id: row.id ?? '', title: row.title ?? '', author: row.author ?? '', date: row.date ?? '',
    origin: row.origin ?? '', url: row.url ?? '', adaptation: row.adaptation ?? '',
  }))
}

export async function loadStudySupports(): Promise<StudySupport[]> {
  return (await fetchContentRows('study_support.csv')).map((row) => ({
    id: row.id ?? '', unit: row.unit ?? '', oa: row.oa ?? '', topic: row.topic ?? '',
    level: row.level ?? 'general',
    difficulty: (row.difficulty ?? 'General') as StudySupport['difficulty'],
    vocabularyLevel: (row.vocabulary_level ?? 'Todos') as StudySupport['vocabularyLevel'],
    supportIntensity: (row.support_intensity ?? 'Todos') as StudySupport['supportIntensity'],
    title: row.title ?? '', summary: row.summary ?? '', notes: parseJson<string[]>(row.notes ?? '', []),
    keyIdeas: parseJson<string[]>(row.key_ideas ?? '', []), keyConcepts: parseJson<string[]>(row.key_concepts ?? '', []),
    historicalExample: row.historical_example ?? '', tableTitle: row.table_title ?? '',
    tableHeaders: parseJson<string[]>(row.table_headers ?? '', []), tableRows: parseJson<string[][]>(row.table_rows ?? '', []),
    imagePath: row.image_path ?? '', sourceId: row.source_id ?? '', notebookActivity: row.notebook_activity ?? '',
    notebookPrompts: parseJson<string[]>(row.notebook_prompts ?? '', []), selfAssessment: parseJson<string[]>(row.self_assessment ?? '', []),
  }))
}

export function validateQuestions(questions: Question[]): void {
  const ids = new Set<string>()
  for (const question of questions) {
    if (!question.id || !question.prompt || !question.oa || !question.topic) throw new Error('Hay una pregunta con campos obligatorios incompletos.')
    if (ids.has(question.id)) throw new Error(`El identificador ${question.id} está repetido.`)
    ids.add(question.id)
    if (['multiple_choice', 'source', 'data'].includes(question.type) && question.options.length !== 4) {
      throw new Error(`La pregunta ${question.id} debe tener cuatro alternativas.`)
    }
    if (question.type === 'true_false' && question.options.length !== 2) throw new Error(`La pregunta ${question.id} debe tener dos alternativas.`)
    if (typeof question.correctAnswer === 'string' && question.options.length > 0 && !question.options.includes(question.correctAnswer)) {
      throw new Error(`La respuesta correcta de ${question.id} no está entre sus alternativas.`)
    }
  }
}
